import { Injectable } from '@angular/core';
import { filter, first, map } from 'rxjs/operators';
import { Logger, LoggerFactory } from '@rillion/core/logger';
import { AppStorage } from '@rillion/core/services';
import { UserModel } from '@rillion/auth/models';
import { registerLocaleData } from '@angular/common';
import localeEnGb from '@angular/common/locales/en-GB';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { AuthSelectors } from '@rillion/store/auth';
import { SettingsActions } from '@rillion/store/settings/settings.actions';

@Injectable({
  providedIn: 'root',
})
export class AppInitializerService {
  private readonly logger: Logger;
  private readonly languages = ['en-GB', 'sv-SE'];
  private readonly themes = ['rillion-light-theme', 'rillion-dark-theme'];

  constructor(
    private readonly store: Store,
    private appStorage: AppStorage,
    private translateService: TranslateService,
    loggerFactory: LoggerFactory,
  ) {
    this.logger = loggerFactory.getLogger('AppInitializerService');
  }

  onAppInit(): Promise<any> {
    this.logger.debug('locationHref:', window.location.href);

    // Prepare languages & theme
    // LOCALE - For dates and currency formats
    registerLocaleData(localeEnGb);
    registerLocaleData(localeEs);
    registerLocaleData(localeFr);

    // TRANSLATIONS - i18n
    this.translateService.addLangs(this.languages);

    // This language will be used as a fallback when a translation isn't found in the current language
    this.translateService.setDefaultLang('en-GB');

    // The lang to use, if the lang isn't available, it will use the current loader to get them
    this.translateService.use('en-GB');

    this.logger.info('Current language:', this.translateService.currentLang);

    // Async dispatches
    this.store.dispatch(SettingsActions.loadSettings());
    this.store.dispatch(SettingsActions.loadTheme({ themes: this.themes }));

    return new Promise((resolve) => {
      this.store
        .select(AuthSelectors.selectUser)
        .pipe(
          filter((user) => !!user),
          first(),
          map((user: UserModel | undefined) => {
            if (user.token) {
              this.logger.info('User is logged in');
            } else {
              this.appStorage.removeDataForKey('USER');
              this.logger.warn('Token does not exist!');
            }
            resolve(true);
          }),
        )
        .subscribe();
    });
  }
}
